import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";
import {
    PROPERTY_STATUS_OPTIONS, PROJECT_TYPE_OPTIONS, BUDGET_OPTIONS, TIMING_OPTIONS,
} from "../../lib/enums";

const inputCls = "w-full px-3 py-2 bg-white border border-charcoal/20 rounded-md font-inter text-[14px] text-charcoal focus:border-terracotta focus:outline-none";
const labelCls = "block font-manrope font-semibold text-charcoal text-[12.5px] mb-1.5";

export default function EnquiryNew() {
    usePageMeta({ title: "New enquiry | Studio G4 Admin" });
    const nav = useNavigate();
    const [d, setD] = useState({
        full_name: "", email: "", phone: "", postcode: "",
        property_status: "", project_types: [], project_description: "",
        construction_budget: "", construction_timing: "", internal_notes: "",
    });
    const [err, setErr] = useState({});
    const [busy, setBusy] = useState(false);

    const set = (k, v) => { setD((x) => ({ ...x, [k]: v })); setErr((e) => ({ ...e, [k]: undefined })); };

    const save = async () => {
        setBusy(true);
        setErr({});
        try {
            const payload = { ...d };
            if (!payload.email) delete payload.email;
            if (!payload.phone) delete payload.phone;
            if (!payload.property_status) delete payload.property_status;
            if (!payload.construction_budget) delete payload.construction_budget;
            if (!payload.construction_timing) delete payload.construction_timing;
            if (!payload.project_description) delete payload.project_description;
            if (!payload.internal_notes) delete payload.internal_notes;

            const { data } = await api.post("/admin/enquiries", payload);
            toast.success("Enquiry created");
            nav(`/admin/projects/${data.id}`);
        } catch (ex) {
            const detail = ex?.response?.data?.detail;
            if (detail?.errors) {
                const e = {}; detail.errors.forEach((x) => { e[x.field] = x.message; }); setErr(e);
            } else if (typeof detail === "string") {
                toast.error(detail);
            } else {
                toast.error("Failed to create enquiry");
            }
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-[720px]" data-testid="enquiry-new-page">
            <Link to="/admin/projects" className="font-inter text-terracotta text-[13px] hover:underline">← Back to projects</Link>
            <h1 className="mt-3 font-manrope font-bold text-charcoal text-[28px] md:text-[32px] mb-2">New enquiry</h1>
            <p className="font-inter text-charcoal/60 text-[13.5px] mb-8">Source will be set to <span className="font-semibold text-charcoal">Manual</span>. Full name, postcode and at least one of email or phone are required — everything else is optional.</p>

            <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Full name *</label>
                        <input value={d.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="new-full-name" />
                        {err.full_name && <p className="text-terracotta text-[12px] mt-1">{err.full_name}</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Postcode *</label>
                        <input value={d.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} className={inputCls} data-testid="new-postcode" />
                        {err.postcode && <p className="text-terracotta text-[12px] mt-1">{err.postcode}</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Email</label>
                        <input type="email" value={d.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="new-email" />
                    </div>
                    <div>
                        <label className={labelCls}>Phone</label>
                        <input type="tel" value={d.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="new-phone" />
                    </div>
                </div>

                <div>
                    <label className={labelCls}>Property status</label>
                    <select value={d.property_status} onChange={(e) => set("property_status", e.target.value)} className={inputCls}>
                        <option value="">—</option>
                        {PROPERTY_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>

                <div>
                    <label className={labelCls}>Project types</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {PROJECT_TYPE_OPTIONS.map((o) => {
                            const checked = d.project_types.includes(o.value);
                            return (
                                <label key={o.value} className={"flex items-center gap-2 px-3 py-2 border rounded cursor-pointer text-[13px] " + (checked ? "border-terracotta bg-terracotta/5" : "border-charcoal/20")}>
                                    <input type="checkbox" checked={checked} onChange={(e) => set("project_types", e.target.checked ? [...d.project_types, o.value] : d.project_types.filter((v) => v !== o.value))} className="accent-terracotta" />
                                    <span>{o.label}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <label className={labelCls}>Description</label>
                    <textarea rows={4} value={d.project_description} onChange={(e) => set("project_description", e.target.value)} className={inputCls + " resize-y"} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Budget</label>
                        <select value={d.construction_budget} onChange={(e) => set("construction_budget", e.target.value)} className={inputCls}>
                            <option value="">—</option>
                            {BUDGET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Timing</label>
                        <select value={d.construction_timing} onChange={(e) => set("construction_timing", e.target.value)} className={inputCls}>
                            <option value="">—</option>
                            {TIMING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label className={labelCls}>Internal notes</label>
                    <textarea rows={4} value={d.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} className={inputCls + " resize-y"} />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                    <Link to="/admin/projects" className="px-4 py-2 text-charcoal/70 hover:text-charcoal font-manrope font-semibold text-[13.5px]">Cancel</Link>
                    <button onClick={save} disabled={busy} className="px-6 py-2.5 rounded-md bg-terracotta text-offwhite font-manrope font-semibold text-[13.5px] hover:bg-terracotta-600 disabled:opacity-70" data-testid="new-save">
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
